/**
 * Opportunity Detail Screen Styles
 * 
 * Extracted to keep screen file under 300 lines per cursor rules.
 * Uses design tokens exclusively.
 */

import { StyleSheet } from 'react-native';
import { color, text, space, radius, icon, layout } from '../../../shared/tokens/mobile';

export const styles = StyleSheet.create({
  scroll: { 
    flex: 1 
  },
  scrollContent: { 
    paddingHorizontal: space.md, 
    paddingBottom: layout.scrollBottomPadding 
  },
  center: { 
    flex: 1, 
    alignItems: 'center', 
    justifyContent: 'center' 
  },
  errorText: { 
    fontSize: text.cardTitle.fontSize, 
    fontWeight: text.cardTitle.fontWeight, 
    color: color.textPrimary, 
    marginTop: space.md 
  },
  
  // Photo Hero
  photoHero: {
    height: 180,
    backgroundColor: color.backgroundSecondary,
    borderRadius: radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: space.md,
    gap: space.sm,
  },
  photoText: { 
    fontSize: text.caption.fontSize, 
    color: color.textMuted 
  },
  
  // Location & Status
  location: { 
    fontSize: text.body.fontSize, 
    color: color.textSecondary, 
    marginBottom: space.sm 
  },
  statusRow: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    marginBottom: space.lg, 
    gap: space.md 
  },
  statusBadge: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    paddingHorizontal: space.sm + space.xs, 
    paddingVertical: space.xs + 2, 
    borderRadius: radius.full, 
    gap: space.xs 
  },
  statusDot: { 
    width: 8, 
    height: 8, 
    borderRadius: 4 
  },
  statusText: { 
    fontSize: text.caption.fontSize, 
    fontWeight: '600' 
  },
  
  // Pass Button
  passButton: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    gap: space.xs 
  },
  passText: { 
    fontSize: text.caption.fontSize, 
    color: color.textMuted 
  },
  
  // Pass Reason Card
  passReasonCard: {
    backgroundColor: color.backgroundSecondary,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.lg,
  },
  passReasonLabel: { 
    fontSize: text.caption.fontSize, 
    color: color.textMuted, 
    marginBottom: space.xs 
  },
  passReasonText: { 
    fontSize: text.body.fontSize, 
    color: color.textSecondary 
  },
  
  // Metrics Card
  metricsCard: { 
    flexDirection: 'row', 
    backgroundColor: color.surface, 
    borderRadius: radius.lg, 
    borderWidth: 1, 
    borderColor: color.border, 
    paddingVertical: space.lg, 
    marginBottom: space.md 
  },
  metric: { 
    flex: 1, 
    alignItems: 'center' 
  },
  metricValue: { 
    fontSize: text.sectionTitle.fontSize, 
    fontWeight: text.sectionTitle.fontWeight, 
    color: color.textPrimary 
  },
  metricLabel: { 
    fontSize: text.caption.fontSize, 
    color: color.textSecondary, 
    marginTop: space.xs 
  },
  
  // Parcel Warning
  parcelWarning: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: color.warningLight,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.md,
    gap: space.sm,
  },
  parcelWarningContent: { 
    flex: 1 
  },
  parcelWarningTitle: { 
    fontSize: text.body.fontSize, 
    fontWeight: text.bodyMedium.fontWeight, 
    color: color.warning 
  },
  parcelWarningSubtitle: { 
    fontSize: text.caption.fontSize, 
    color: color.textSecondary, 
    marginTop: 2 
  },
  
  // Listing Button
  listingButton: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    justifyContent: 'center', 
    gap: space.sm, 
    paddingVertical: space.sm + space.xs, 
    backgroundColor: color.infoLight, 
    borderRadius: radius.md, 
    marginBottom: space.lg 
  },
  listingText: { 
    fontSize: text.body.fontSize, 
    fontWeight: text.bodyMedium.fontWeight, 
    color: color.info 
  },
  
  // Section
  section: { 
    marginBottom: space.lg 
  },
  
  // Ideas Placeholder
  ideasPlaceholder: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: color.primaryLight,
    borderRadius: radius.lg,
    padding: space.md,
    gap: space.sm,
  },
  ideasContent: { 
    flex: 1 
  },
  ideasTitle: { 
    fontSize: text.body.fontSize, 
    fontWeight: text.bodyMedium.fontWeight, 
    color: color.primary 
  },
  ideasSubtitle: { 
    fontSize: text.caption.fontSize, 
    color: color.textSecondary, 
    marginTop: 2 
  },
  
  // Price Section
  priceSection: {
    backgroundColor: color.surface,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: color.border,
    padding: space.md,
    marginBottom: space.md,
  },
  priceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: space.sm,
    borderBottomWidth: 1,
    borderBottomColor: color.border,
  },
  priceLabel: {
    fontSize: text.body.fontSize,
    color: color.textSecondary,
  },
  priceValues: {
    alignItems: 'flex-end',
  },
  priceTotal: {
    fontSize: text.sectionTitle.fontSize,
    fontWeight: text.sectionTitle.fontWeight,
    color: color.textPrimary,
  },
  pricePerAcre: {
    fontSize: text.caption.fontSize,
    color: color.textMuted,
    marginTop: 2,
  },
  
  // View Map Button
  viewMapButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: space.sm,
    paddingVertical: space.sm + space.xs,
    backgroundColor: color.primaryLight,
    borderRadius: radius.md,
    marginBottom: space.md,
  },
  viewMapText: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.primary,
  },
  
  // Parcel Card
  parcelCard: {
    backgroundColor: color.surface,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: color.border,
    padding: space.md,
    marginBottom: space.md,
  },
  parcelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
    marginBottom: space.md,
    paddingBottom: space.sm,
    borderBottomWidth: 1,
    borderBottomColor: color.border,
  },
  parcelTitle: {
    flex: 1,
    fontSize: text.sectionTitle.fontSize,
    fontWeight: text.sectionTitle.fontWeight,
    color: color.textPrimary,
  },
  parcelRefresh: {
    padding: space.xs,
  },
  parcelDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: space.sm,
    borderBottomWidth: 1,
    borderBottomColor: color.border,
  },
  parcelDetailLabel: {
    fontSize: text.body.fontSize,
    color: color.textSecondary,
  },
  parcelDetailValue: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
    flex: 1,
    textAlign: 'right',
    marginLeft: space.md,
  },
  
  // County Record Links
  countyLinksContainer: {
    marginTop: space.md,
    paddingTop: space.sm,
    borderTopWidth: 1,
    borderTopColor: color.border,
  },
  countyLinksTitle: {
    fontSize: text.caption.fontSize,
    color: color.textMuted,
    marginBottom: space.sm,
  },
  countyLinksRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: space.sm,
  },
  countyLinkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.xs,
    paddingHorizontal: space.sm,
    paddingVertical: space.xs,
    backgroundColor: color.infoLight,
    borderRadius: radius.sm,
  },
  countyLinkText: {
    fontSize: text.caption.fontSize,
    color: color.info,
    fontWeight: '500',
  },
});

